import { makeAutoObservable, runInAction } from 'mobx'
import {
  getCategoryGroupTranslation,
  getCategoryTranslations,
  getColorTranslation,
} from 'page/StartScreen/combine/utils'
import { api } from 'store/api'
import { CheckStore } from 'store/item/CheckStore'
import { PriceStore } from 'store/item/PriceStore'
import { opt } from 'store/opt'
import { Item } from 'type/Item'
import { Brand, Category, FiltersResponse, OfferItem, RecommendationsRequest } from 'type/rec'
import { skipAsync } from 'util/async'
import { flatMap } from 'util/iterable'
import { matchSearch } from 'util/search'
import { by } from 'util/sort'
import { compareIgnoreCase } from 'util/string'

export type State = 'none' | 'loading' | 'ready' | 'error'

function getComboShape(item: Item) {
  return item.dress_shape_type === 'bottom' ? 'top' : 'bottom'
}

function getComboGender(item: Item) {
  const gender = item.dress_gender ?? ''
  if (['male', 'female'].includes(gender)) return gender
  return undefined
}

export class RecStore {
  private readonly json: Item
  private _state: State = 'none'
  private _male = false

  private _init = false

  readonly brand = new CheckStore()
  readonly color = new CheckStore()
  readonly category = new CheckStore<Category>()

  readonly min_price = new PriceStore()
  readonly max_price = new PriceStore()

  private _brands: Brand[] = []
  private _colors: string[] = []
  private _categories: Category[] = []

  private all: OfferItem[] = []
  private _index = 0

  constructor(json: Item) {
    makeAutoObservable(this)
    this.json = json
  }

  get init(): boolean {
    return this._init
  }

  get line(): boolean {
    return this.state === 'ready' && !!this.items.length
  }

  get state(): State {
    return this._state
  }

  get male(): boolean {
    return this._male
  }

  set male(value: boolean) {
    this._male = value
  }

  get brands(): Brand[] {
    return this._brands
  }

  get brands_filtered(): Brand[] {
    return this._brands.filter((it) =>
      matchSearch(this.brand.search, [it.title]),
    )
  }

  get colors(): string[] {
    return this._colors
  }

  get colors_filtered(): string[] {
    return this._colors.filter((it) =>
      matchSearch(this.color.search, [getColorTranslation(it)]),
    )
  }

  get categories(): Category[] {
    return this._categories
  }

  get categories_filtered(): Category[] {
    return this._categories.filter((it) =>
      matchSearch(this.category.search, [
        getCategoryGroupTranslation(it.group),
        getCategoryTranslations(it.name),
      ]),
    )
  }

  get index(): number {
    return this._index
  }

  get item(): OfferItem | undefined {
    return this.items[this._index]
  }

  set item(item: OfferItem | undefined) {
    const index = item ? this.items.indexOf(item) : 0
    this.index = index < 0 ? 0 : index
  }

  set index(index: number) {
    const max = this.items.length - 1
    this._index = Math.max(0, Math.min(max, index))
  }

  get items(): OfferItem[] {
    return this.all
  }

  reset() {
    this.brand.reset()
    this.brand.apply()
    this.color.reset()
    this.color.apply()
    this.category.reset()
    this.category.apply()
    this.min_price.reset()
    this.max_price.reset()
  }

  readonly load = skipAsync(async () => {
    runInAction(() => (this._state = 'loading'))
    try {
      const filters_ = this.getFilters()
      const items_ = this.getRecommendationsSafe({})

      const items = await items_

      const filters = await filters_.catch((e) => {
        console.error('load filters failed', e)
        return undefined
      })
      const { brands, colors, categories } = filters || {}

      runInAction(() => {
        this.all = items
        this._brands = brands?.sort(by((it) => it.name.toLowerCase())) ?? []
        this._colors = colors?.sort(compareIgnoreCase) ?? []
        this._categories = this.extractCategories(categories ?? [])
        this._index = 0
        this._state = 'ready'
        this._init = true
      })
    } catch (e) {
      console.error('load failed', e)
      runInAction(() => (this._state = 'error'))
    }
  })

  search = skipAsync(async () => {
    runInAction(() => {
      this.all = []
      this._state = 'loading'
    })
    try {
      const brand = this.brand.value.size
        ? Array.from(this.brand.value)
        : undefined
      const color = this.color.value.size
        ? Array.from(this.color.value)
        : undefined
      const category = this.category.value.size
        ? flatMap(this.category.value, (it) => it.ids)
        : undefined
      const price_from = this.min_price.value == null ? undefined : this.min_price.value
      const price_to = this.max_price.value == null ? undefined : this.max_price.value
      const request = { brand, color, category, price_from, price_to }
      const items = await this.getRecommendationsSafe(request)

      runInAction(() => {
        this.all = items
        this._index = 0
        this._state = 'ready'
      })
    } catch (e) {
      console.error('search failed', e)
      runInAction(() => (this._state = 'error'))
    }
  })

  private get tsum(): boolean {
    return opt.company === 'tsum_cs'
  }

  private get combine() {
    const combine = this.tsum ? this.json.combine : getComboShape(this.json)
    if (!combine) throw new Error('no combine')
    return combine
  }

  private get gender() {
    return getComboGender(this.json)
  }

  private async getFilters(): Promise<FiltersResponse> {
    try {
      return api.getFilters(this.combine, this.gender)
    } catch (e) {
      console.error('get filter failed', e)
      return {}
    }
  }

  private async getRecommendationsSafe(base: Record<string, unknown>): Promise<OfferItem[]> {
    try {
      const { combine, gender } = this
      const { external_item_id } = this.json
      const group = opt.options.window?.group
      if (this.tsum && !external_item_id) throw new Error('no external_item_id')
      const request: RecommendationsRequest = { group, external_item_id, combine, gender, ...base }
      return await api.getRecommendations(request)
    } catch (e) {
      console.error('get recommendations failed', e)
      return []
    }
  }

  private extractCategories(categories: Category[]) {
    return categories
      .filter((it) => this.filterCategory(it))
      .sort(by((it) => [it.group, it.name].join(' | ').toLowerCase()))
  }

  private filterCategory(category: Category): boolean {
    const group = category.group.toLowerCase()
    if (this.male && group.startsWith('жен')) return false
    if (!this.male && group.startsWith('муж')) return false
    return true
  }
}
